// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useMemo, useState } from 'react';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { Box, ClickAwayListener, Fab, Fade, MenuItem, MenuList, Paper, Popper, Typography, Tooltip } from '@mui/material';
import { useTheme } from '@mui/system';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CreateFolderDialog from 'Components/CreateFolderDialog';
import { useAppDispatch } from 'Hooks';
import { useNavigate } from 'react-router';
import { GetFolderDetailsRoute } from 'Utils/routes';
import DeleteFolderDialog from 'Components/DeleteFolderDialog';
import stylesDef from './styles';
import { deleteFolderThunk } from '../folderListThunk';
import { useDroppable } from '@dnd-kit/core';
import { SetSelectedFolderAction } from '../folderListActions';

interface IFolderListItemProps {
  folder: IFolder;
  showMapview: boolean;
}

const FolderListItem = ({ folder, showMapview }: IFolderListItemProps) => {
  // Constants
  const theme = useTheme();
  const styles = stylesDef(theme, showMapview);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [showDeleteFolderModal, setShowDeleteFolderModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // useMemo
  const isMenuOpen = useMemo(() => Boolean(anchorEl), [anchorEl]);

  // Methods
  const handleMenuClose = () => setAnchorEl(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => setAnchorEl(event.currentTarget);

  const renameFolderClick = () => {
    setShowRenameFolderModal(true);
  };

  const deleteFolderClick = async () => {
    if (folder?.projectCount > 0) {
      setShowDeleteFolderModal(true);
    } else {
      deleteFolder(folder?._id);
    }
  };

  const deleteFolder = async (folderId: string) => {
    setLoading(true);
    // Call the delete folder thunk or API here
    const result = await dispatch(deleteFolderThunk(folderId));

    // If the deletion is successful, fetch all folders again
    if (result.success) {
      // Close the delete confirmation modal
      setShowDeleteFolderModal(false);
      setLoading(false);
    }
  };

  const handleFolderClick = () => {
    if (!folder?._id) return;

    dispatch(SetSelectedFolderAction(folder));

    const url = GetFolderDetailsRoute(folder._id);
    navigate(url);
  };

  const { isOver, setNodeRef } = useDroppable({
    id: folder._id,
  });

  return (
    <>
      <Box
        ref={setNodeRef}
        sx={{
          ...styles.mainContiner,
          ...(isOver && styles.activeDroppableHover)
        }}
        onClick={handleFolderClick}
      >
        <Box sx={styles.folderItemContainer}>
          <Box sx={styles.folderIconContainer}>
            <FolderOutlinedIcon />
          </Box>
          <Box sx={styles.folderDetails}>
            {folder?.name?.length > 26 ? (
              <Tooltip title={folder.name} placement="top" arrow>
                <Typography variant="h5" sx={styles.folderNameText}>
                  {folder.name ?? ''}
                </Typography>
              </Tooltip>
            ) : (
              <Typography variant="h5" sx={styles.folderNameText}>
                {folder.name ?? ''}
              </Typography>
            )}
            <Typography variant="body2" sx={styles.projectCount}>{`${folder?.projectCount ?? 0} ${folder?.projectCount > 1 ? 'Projects' : 'Project'}`}</Typography>
          </Box>
        </Box>
        <Fab
          sx={styles.moreIconContainer}
          className={isMenuOpen ? 'showFab' : ''}
          onClick={(e) => {
            e.stopPropagation();
            handleMenuOpen(e);
          }}
        >
          <MoreHorizIcon />
        </Fab>
        <Popper sx={styles.folderListItemPopper} open={isMenuOpen} anchorEl={anchorEl} transition>
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={handleMenuClose}>
              <Fade {...TransitionProps} timeout={350}>
                <Paper>
                  <MenuList sx={styles.menuList}>
                    <MenuItem
                      sx={styles.fontWeight}
                      onClick={async (e) => {
                        e.stopPropagation();
                        // Add Pendo Track Events
                        window?.pendo?.track('Rename Folder');
                        handleMenuClose();
                        await renameFolderClick();
                      }}
                    >
                      Rename
                    </MenuItem>
                    <MenuItem
                      sx={styles.fontWeight}
                      onClick={async (e) => {
                        e.stopPropagation();
                        // Add Pendo Track Events
                        window?.pendo?.track('Delete Folder');
                        handleMenuClose();
                        await deleteFolderClick();
                      }}
                    >
                      Delete
                    </MenuItem>
                  </MenuList>
                </Paper>
              </Fade>
            </ClickAwayListener>
          )}
        </Popper>
      </Box>

      {/* Create/Rename Folder Dialog */}
      <CreateFolderDialog
        showDialog={showRenameFolderModal}
        title="Rename Folder"
        isEdit
        folderName={folder?.name}
        folderId={folder?._id}
        setShowDialog={setShowRenameFolderModal}
      />

      {/* Delete Folder Dialog */}
      <DeleteFolderDialog
        showDialog={showDeleteFolderModal}
        setShowDialog={setShowDeleteFolderModal}
        onDelete={() => deleteFolder(folder?._id)}
        loading={loading}
      />
    </>
  );
};

export default FolderListItem;
